/**
 * Header Component - Mobile-First Responsive
 * Baseado em best practices 2025: Headless UI Disclosure, Tailwind CSS
 *
 * Padrão:
 * - Mobile: Hamburger menu + logo compacto
 * - Desktop (md:768px+): Full horizontal navbar
 *
 * Referências:
 * - https://headlessui.com/react/disclosure
 * - https://dev.to/chintanonweb/the-ultimate-guide-to-crafting-a-responsive-header-with-react-and-tailwind-css-o7g
 */

import { Fragment } from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

interface HeaderProps {
  onNewInvestigation?: () => void;
}

export function Header({ onNewInvestigation }: HeaderProps) {
  return (
    <Disclosure as="header" className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      {({ open, close }) => (
        <>
          {/* Container principal */}
          <div className="w-full px-4 sm:px-6 lg:max-w-7xl lg:mx-auto">
            <div className="flex justify-between items-center h-14 sm:h-16">

              {/* Logo/Title - Sempre visível */}
              <div className="flex items-center min-w-0">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600 truncate">
                  {/* Mobile: título curto */}
                  <span className="md:hidden">RF Investigações</span>
                  {/* Desktop: título completo */}
                  <span className="hidden md:inline">Receita Federal - Investigações</span>
                </h1>
              </div>

              {/* Desktop Navigation - Escondido em mobile */}
              <nav className="hidden md:flex items-center space-x-4">
                <a
                  href="#investigacoes"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors"
                >
                  Investigações
                </a>
                <a
                  href="#grafo"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors"
                >
                  Explorar Grafo
                </a>
                <button
                  onClick={onNewInvestigation}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px]"
                >
                  Nova Investigação
                </button>
              </nav>

              {/* Mobile Menu Button - Visível apenas em mobile */}
              <div className="flex md:hidden">
                <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 min-h-[44px] min-w-[44px]">
                  <span className="sr-only">Abrir menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* Mobile Menu Panel - Animado */}
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Disclosure.Panel className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">

                {/* Link com auto-close ao clicar (usando Disclosure.Button) */}
                <Disclosure.Button
                  as="a"
                  href="#investigacoes"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 min-h-[44px] flex items-center"
                >
                  Investigações
                </Disclosure.Button>

                <Disclosure.Button
                  as="a"
                  href="#grafo"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 min-h-[44px] flex items-center"
                >
                  Explorar Grafo
                </Disclosure.Button>

                {/* Botão de ação - fecha menu e executa callback */}
                <Disclosure.Button
                  as="button"
                  onClick={() => {
                    close(); // Fecha menu
                    onNewInvestigation?.(); // Executa callback
                  }}
                  className="w-full text-left block px-3 py-2 rounded-md text-base font-medium bg-blue-600 text-white hover:bg-blue-700 min-h-[44px] flex items-center justify-center mt-2"
                >
                  Nova Investigação
                </Disclosure.Button>
              </div>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}
